import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Handshake, Settings, BarChart3, ArrowRight, Check, Crown, Puzzle, MessageCircle, ChevronDown, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { lazy, Suspense } from "react";
const ContactForm = lazy(() => import("./ContactForm").then((m) => ({ default: m.ContactForm })));
import { useCmsText } from "@/hooks/useCmsText";
import { safeHtml } from "@/lib/safeHtml";

export function ServicesSection() {
  const { t } = useTranslation();
  const cms = useCmsText("landing");
  const navigate = useNavigate();
  const [isContactOpen, setIsContactOpen] = useState(false);
  const [expandedPhase, setExpandedPhase] = useState<number | null>(null);

  const phases = [
    { number: "01", key: "phase1", icon: Search, path: "/leistungen/markteintritt" },
    { number: "02", key: "phase2", icon: Handshake, path: "/leistungen/marktzugang" },
    { number: "03", key: "phase3", icon: Settings, path: "/leistungen/operative-umsetzung" },
    { number: "04", key: "phase4", icon: BarChart3, path: "/leistungen/marktvalidierung" },
    { number: "05", key: "phase5", icon: Crown, isPremium: true, path: "/leistungen/vollzugang-europa" },
    { number: "06", key: "phase6", icon: Puzzle, path: "/leistungen/zusatzleistungen" },
  ];

  const getContents = (phaseKey: string): string[] => {
    const raw = t(`services.phases.${phaseKey}.contents`, { returnObjects: true });
    if (Array.isArray(raw)) {
      return raw.filter((item): item is string => typeof item === 'string');
    }
    return [];
  };

  const togglePhase = (index: number) => {
    setExpandedPhase(expandedPhase === index ? null : index);
  };

  return (
    <section id="leistungen" className="py-24 md:py-32 lg:py-40 bg-[hsl(180,10%,97%)] overflow-hidden">
      <div className="w-full px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        {/* Section Header - Editorial style */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20 max-w-3xl mx-auto px-2"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-display mb-6 text-foreground">
            {cms("services.badge", t("services.badge"))}
          </h2>
          <p className="text-xl text-muted-foreground mb-4">
            {cms("services.subtitle", t("services.subtitle"))}
          </p>
          <p className="text-muted-foreground">
            <span dangerouslySetInnerHTML={{ __html: safeHtml(cms("services.intro.text1", t("services.intro.text1"))) }} />
          </p>
        </motion.div>

        {/* Phase Cards - Interactive Accordion Style */}
        <div className="max-w-5xl mx-auto grid gap-4 sm:gap-6">
          {phases.map((phase, index) => {
            const Icon = phase.icon;
            const contents = getContents(phase.key);
            const isPremium = phase.isPremium;
            const isExpanded = expandedPhase === index;
            
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.08 * index, duration: 0.5 }}
                layout
              >
                <div 
                  className={`group relative bg-background rounded-xl overflow-hidden transition-all duration-500 cursor-pointer
                    ${isExpanded 
                      ? isPremium 
                        ? 'shadow-[0_8px_40px_-12px_hsl(38,65%,55%,0.3)] ring-2 ring-[hsl(38,65%,55%)]' 
                        : 'shadow-[0_8px_40px_-12px_hsl(185,65%,18%,0.25)] ring-2 ring-[hsl(185,65%,18%)]'
                      : 'shadow-sm hover:shadow-lg hover:shadow-mh-teal/10'
                    }
                  `}
                  onClick={() => togglePhase(index)}
                >
                  {/* Premium Badge Ribbon */}
                  {isPremium && (
                    <div className="absolute top-0 right-0 bg-gradient-to-l from-[hsl(38,65%,55%)] to-[hsl(38,70%,60%)] text-white text-[10px] font-bold uppercase tracking-wider px-4 py-1.5 rounded-bl-lg">
                      Premium
                    </div>
                  )}

                  {/* Collapsed Header */}
                  <div className={`p-4 sm:p-6 md:p-8 transition-colors duration-300 ${
                    isExpanded 
                      ? isPremium 
                        ? 'bg-gradient-to-r from-[hsl(38,65%,55%,0.08)] to-transparent' 
                        : 'bg-gradient-to-r from-[hsl(185,65%,18%,0.06)] to-transparent'
                      : 'hover:bg-muted/30'
                  }`}>
                    <div className="flex items-center gap-3 sm:gap-5">
                      {/* Icon Container */}
                      <div className={`relative flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-xl sm:rounded-2xl flex items-center justify-center transition-all duration-500
                        ${isExpanded
                          ? isPremium
                            ? 'bg-gradient-to-br from-[hsl(38,65%,55%)] to-[hsl(38,70%,45%)] shadow-lg shadow-[hsl(38,65%,55%,0.3)]'
                            : 'bg-gradient-to-br from-[hsl(185,65%,18%)] to-[hsl(185,70%,25%)] shadow-lg shadow-[hsl(185,65%,18%,0.3)]'
                          : isPremium
                            ? 'bg-[hsl(38,65%,55%,0.12)] group-hover:bg-[hsl(38,65%,55%,0.2)]'
                            : 'bg-[hsl(185,65%,18%,0.08)] group-hover:bg-[hsl(185,65%,18%,0.15)]'
                        }
                      `}>
                        <Icon className={`w-5 h-5 sm:w-7 sm:h-7 md:w-8 md:h-8 transition-colors duration-300 ${
                          isExpanded 
                            ? 'text-white' 
                            : isPremium 
                              ? 'text-[hsl(38,65%,50%)]' 
                              : 'text-[hsl(185,65%,18%)]'
                        }`} />
                        {/* Phase Number Badge */}
                        <span className={`absolute -top-1 -left-1 sm:-top-2 sm:-left-2 w-5 h-5 sm:w-7 sm:h-7 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold
                          ${isPremium 
                            ? 'bg-[hsl(38,65%,55%)] text-white' 
                            : 'bg-[hsl(185,65%,18%)] text-white'
                          }
                        `}>
                          {phase.number}
                        </span>
                      </div>

                      {/* Title Area */}
                      <div className="flex-1 min-w-0">
                        <span className={`text-xs font-semibold uppercase tracking-wider ${
                          isPremium ? 'text-[hsl(38,65%,50%)]' : 'text-[hsl(180,60%,40%)]'
                        }`}>
                          {t(`services.phases.${phase.key}.phase`)}
                        </span>
                        <h3 className="text-base sm:text-lg md:text-xl font-display text-foreground mt-1 line-clamp-2 sm:truncate">
                          {t(`services.phases.${phase.key}.title`)}
                        </h3>
                        <p className={`text-xs sm:text-sm mt-1 line-clamp-2 ${isPremium ? 'text-[hsl(38,65%,50%)]' : 'text-muted-foreground'}`}>
                          {t(`services.phases.${phase.key}.subtitle`)}
                        </p>
                      </div>

                      {/* Expand Indicator */}
                      <div className={`flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-all duration-300
                        ${isExpanded 
                          ? isPremium 
                            ? 'bg-[hsl(38,65%,55%)] rotate-180' 
                            : 'bg-[hsl(185,65%,18%)] rotate-180'
                          : 'bg-muted group-hover:bg-muted-foreground/20'
                        }
                      `}>
                        <ChevronDown className={`w-5 h-5 transition-colors ${isExpanded ? 'text-white' : 'text-muted-foreground'}`} />
                      </div>
                    </div>
                  </div>

                  {/* Expandable Content */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                        className="overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="px-4 pb-6 sm:px-6 sm:pb-8 md:px-8 md:pb-10 pt-2">
                          <div className="border-t border-border/50 pt-6">
                            {/* Contents Title */}
                            <h4 className={`text-sm font-bold uppercase tracking-wider mb-5 ${
                              isPremium ? 'text-[hsl(38,65%,45%)]' : 'text-[hsl(185,65%,18%)]'
                            }`}>
                              {t(`services.phases.${phase.key}.contentsTitle`)}
                            </h4>
                            
                            {/* Contents Grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3 mb-6 sm:mb-8">
                              {contents.map((item, idx) => (
                                <motion.div 
                                  key={idx}
                                  initial={{ opacity: 0, x: -10 }}
                                  animate={{ opacity: 1, x: 0 }}
                                  transition={{ delay: 0.05 * idx, duration: 0.3 }}
                                  className={`flex items-start gap-3 p-3 rounded-lg transition-colors
                                    ${isPremium 
                                      ? 'bg-[hsl(38,65%,55%,0.06)] hover:bg-[hsl(38,65%,55%,0.12)]' 
                                      : 'bg-[hsl(185,65%,18%,0.04)] hover:bg-[hsl(185,65%,18%,0.08)]'
                                    }
                                  `}
                                >
                                  <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5
                                    ${isPremium 
                                      ? 'bg-[hsl(38,65%,55%)]' 
                                      : 'bg-[hsl(180,60%,40%)]'
                                    }
                                  `}>
                                    <Check className="w-3 h-3 text-white" />
                                  </div>
                                  <span className="text-sm text-foreground/80">{item}</span>
                                </motion.div>
                              ))}
                            </div>
                            
                            {/* Result */}
                            {t(`services.phases.${phase.key}.resultText`) && (
                              <div className={`flex items-start gap-4 p-4 rounded-xl mb-6
                                ${isPremium 
                                  ? 'bg-gradient-to-r from-[hsl(38,65%,55%,0.1)] to-[hsl(38,65%,55%,0.02)] border border-[hsl(38,65%,55%,0.2)]' 
                                  : 'bg-gradient-to-r from-[hsl(185,65%,18%,0.08)] to-transparent border border-[hsl(185,65%,18%,0.15)]'
                                }
                              `}>
                                <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center
                                  ${isPremium 
                                    ? 'bg-[hsl(38,65%,55%)]' 
                                    : 'bg-[hsl(185,65%,18%)]'
                                  }
                                `}>
                                  <ArrowRight className="w-5 h-5 text-white" />
                                </div>
                                <div>
                                  <span className={`text-xs font-bold uppercase tracking-wider
                                    ${isPremium ? 'text-[hsl(38,65%,50%)]' : 'text-[hsl(180,60%,40%)]'}
                                  `}>
                                    {t(`services.phases.${phase.key}.result`)}
                                  </span>
                                  <p className="text-sm text-foreground mt-1">
                                    {t(`services.phases.${phase.key}.resultText`)}
                                  </p>
                                </div>
                              </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row gap-3">
                              <Button
                                onClick={() => setIsContactOpen(true)}
                                className={`flex-1 sm:flex-none transition-all duration-300 ${
                                  isPremium 
                                    ? 'bg-gradient-to-r from-[hsl(38,65%,55%)] to-[hsl(38,70%,50%)] hover:from-[hsl(38,65%,50%)] hover:to-[hsl(38,70%,45%)] text-white shadow-lg shadow-[hsl(38,65%,55%,0.25)]' 
                                    : 'bg-gradient-to-r from-[hsl(185,65%,18%)] to-[hsl(185,60%,25%)] hover:from-[hsl(185,65%,15%)] hover:to-[hsl(185,60%,22%)] text-white shadow-lg shadow-[hsl(185,65%,18%,0.2)]'
                                }`}
                              >
                                <MessageCircle className="h-4 w-4 me-2" />
                                {cms("services.cta", t("services.cta"))}
                              </Button>
                              <Button
                                variant="outline"
                                onClick={() => navigate(phase.path)}
                                className={`flex-1 sm:flex-none border-2 ${
                                  isPremium 
                                    ? 'border-[hsl(38,65%,55%)] text-[hsl(38,65%,45%)] hover:bg-[hsl(38,65%,55%,0.1)]' 
                                    : 'border-[hsl(185,65%,18%)] text-[hsl(185,65%,18%)] hover:bg-[hsl(185,65%,18%,0.08)]'
                                }`}
                              >
                                <ExternalLink className="h-4 w-4 me-2" />
                                Mehr erfahren
                              </Button>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {isContactOpen && (
        <Suspense fallback={null}>
          <ContactForm open={isContactOpen} onOpenChange={setIsContactOpen} />
        </Suspense>
      )}
    </section>
  );
}
